// Menu data layer.
//
// Firestore layout (new):
//   menu_sections/{sectionId}  { order, showDescriptions, i18n: { en: { title }, fr, es, tr } }
//   menu_dishes/{dishId}       { section, order, soldOut, i18n: { en: { name, description }, ... } }
//   menu_menus/{menuId}        { name, dishIds: [], createdAt }
//   menu_config/site           { liveMenuId, hideSoldOut, showSearch, i18n: { en: { title, subtitle, date, footer }, ... } }
//
// Dishes are written once (the "répertoire"); menus only reference dish ids.
// Until the import has run, the guest page falls back to the legacy
// `menu_translations` document, converted in memory to the same shape.

import {
  collection, doc, getDocs, onSnapshot, setDoc, updateDoc, deleteDoc,
  writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';

export const LANGS = ['en', 'fr', 'es', 'tr'];
export const LANG_LABELS = { en: 'EN', fr: 'FR', es: 'ES', tr: 'TR' };

export const COL = {
  sections: 'menu_sections',
  dishes: 'menu_dishes',
  menus: 'menu_menus',
  config: 'menu_config',
  reservations: 'menu_reservations',
};
const CONFIG_ID = 'site';

// Sections whose dish descriptions were shown on the old site.
const DESCRIBED_SECTIONS = ['les-entr-es', 'les-plats-principaux', 'les-desserts', 'les-cocktails'];

export const emptyI18n = (fields) =>
  LANGS.reduce((acc, l) => ({ ...acc, [l]: fields.reduce((o, f) => ({ ...o, [f]: '' }), {}) }), {});

// Text in the requested language, falling back to FR then EN (the house originals).
export const pick = (i18n, lang, field) =>
  i18n?.[lang]?.[field] || i18n?.fr?.[field] || i18n?.en?.[field] || '';

// A dish "needs translation" when some language has text and another is empty.
export const missingLangs = (dish) => {
  const out = [];
  ['name', 'description'].forEach((f) => {
    const filled = LANGS.filter((l) => (dish.i18n?.[l]?.[f] || '').trim());
    if (filled.length && filled.length < LANGS.length) {
      LANGS.forEach((l) => { if (!filled.includes(l) && !out.includes(l)) out.push(l); });
    }
  });
  return out;
};

// Turkish-aware case folding for search (toLowerCase alone mishandles İ/I).
export const fold = (s) =>
  (s || '')
    .replace(/İ/g, 'i').replace(/I/g, 'ı')
    .toLocaleLowerCase('tr')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/ı/g, 'i');

const slug = (s) => fold(s).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// ---------- legacy conversion ----------

export const legacyToModel = (legacy) => {
  const rawSections = legacy.sections || [];
  // Old site showed desserts right after mains.
  const ids = rawSections.map((s) => s.id);
  const mainIdx = ids.indexOf('les-plats-principaux');
  const desIdx = ids.indexOf('les-desserts');
  let ordered = [...rawSections];
  if (mainIdx !== -1 && desIdx !== -1 && desIdx !== mainIdx + 1) {
    const [des] = ordered.splice(desIdx, 1);
    ordered.splice(ordered.findIndex((s) => s.id === 'les-plats-principaux') + 1, 0, des);
  }

  const sections = [];
  const dishes = [];
  ordered.forEach((s, si) => {
    const i18n = {};
    LANGS.forEach((l) => { i18n[l] = { title: s.translations?.[l]?.title || '' }; });
    sections.push({ id: s.id, order: si, showDescriptions: DESCRIBED_SECTIONS.includes(s.id), i18n });
    (s.courses || []).forEach((c, ci) => {
      const di = {};
      LANGS.forEach((l) => {
        di[l] = { name: c.translations?.[l]?.name || '', description: c.translations?.[l]?.description || '' };
      });
      dishes.push({ id: `${s.id}--${slug(c.id) || ci}`, section: s.id, order: ci, soldOut: false, i18n: di });
    });
  });

  const siteI18n = {};
  LANGS.forEach((l) => {
    const t = legacy.translations?.[l] || {};
    siteI18n[l] = {
      title: t.title || legacy.originalTitle || '',
      subtitle: t.subtitle || legacy.originalSubtitle || '',
      date: t.date || legacy.originalDate || '',
      footer: t.footer || legacy.originalFooter || '',
    };
  });

  const menu = { id: 'carte', name: 'La Carte', dishIds: dishes.map((d) => d.id) };
  const config = { liveMenuId: menu.id, hideSoldOut: false, showSearch: true, i18n: siteI18n };
  return { sections, dishes, menus: [menu], config };
};

export const fetchLegacy = async () => {
  const snap = await getDocs(collection(db, 'menu_translations'));
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
};

// Writes the converted legacy menu into the new collections. The legacy
// document itself is left untouched.
export const importLegacy = async () => {
  const legacy = await fetchLegacy();
  if (!legacy) throw new Error('menu_translations bulunamadı');
  const m = legacyToModel(legacy);
  const batch = writeBatch(db);
  m.sections.forEach(({ id, ...rest }) => batch.set(doc(db, COL.sections, id), rest));
  m.dishes.forEach(({ id, ...rest }) => batch.set(doc(db, COL.dishes, id), { ...rest, updatedAt: serverTimestamp() }));
  m.menus.forEach(({ id, ...rest }) => batch.set(doc(db, COL.menus, id), { ...rest, createdAt: serverTimestamp() }));
  batch.set(doc(db, COL.config, CONFIG_ID), m.config);
  await batch.commit();
  return { sections: m.sections.length, dishes: m.dishes.length };
};

// ---------- live subscription ----------

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

// Subscribes to all four collections; calls cb({ sections, dishes, menus, config, source }).
// If the new collections are empty, delivers the legacy menu (source: 'legacy').
export const subscribeAll = (cb, onError) => {
  const state = { sections: null, dishes: null, menus: null, config: undefined };
  let legacyLoaded = false;

  const emit = () => {
    if (state.sections === null || state.dishes === null || state.menus === null || state.config === undefined) return;
    if (!state.dishes.length && !legacyLoaded) {
      legacyLoaded = true;
      fetchLegacy()
        .then((legacy) => {
          if (!legacy) return cb({ sections: [], dishes: [], menus: [], config: null, source: 'empty' });
          cb({ ...legacyToModel(legacy), source: 'legacy' });
        })
        .catch(onError);
      return;
    }
    if (!state.dishes.length) return;
    cb({
      sections: [...state.sections].sort(byOrder),
      dishes: [...state.dishes].sort(byOrder),
      menus: state.menus,
      config: state.config,
      source: 'live',
    });
  };

  const list = (name, key) =>
    onSnapshot(collection(db, name), (snap) => {
      state[key] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      emit();
    }, onError);

  const unsubs = [
    list(COL.sections, 'sections'),
    list(COL.dishes, 'dishes'),
    list(COL.menus, 'menus'),
    onSnapshot(doc(db, COL.config, CONFIG_ID), (snap) => {
      state.config = snap.exists() ? snap.data() : null;
      emit();
    }, onError),
  ];
  return () => unsubs.forEach((u) => u());
};

// Sections + dishes of one menu, in display order.
export const menuView = ({ sections, dishes, menus }, menuId) => {
  const menu = menus.find((m) => m.id === menuId) || menus[0];
  const inMenu = new Set(menu?.dishIds || []);
  return sections
    .map((s) => ({ ...s, dishes: dishes.filter((d) => d.section === s.id && inMenu.has(d.id)) }))
    .filter((s) => s.dishes.length);
};

// ---------- writes ----------

export const saveDish = (id, data) =>
  setDoc(doc(db, COL.dishes, id), { ...data, updatedAt: serverTimestamp() }, { merge: true });

export const setSoldOut = (id, soldOut) => updateDoc(doc(db, COL.dishes, id), { soldOut });

export const resetSoldOut = async (dishIds) => {
  const batch = writeBatch(db);
  dishIds.forEach((id) => batch.update(doc(db, COL.dishes, id), { soldOut: false }));
  await batch.commit();
};

export const newDishId = (sectionId) => `${sectionId}--${Date.now().toString(36)}`;

export const deleteDish = async (id, menus) => {
  const batch = writeBatch(db);
  batch.delete(doc(db, COL.dishes, id));
  menus.filter((m) => (m.dishIds || []).includes(id)).forEach((m) =>
    batch.update(doc(db, COL.menus, m.id), { dishIds: m.dishIds.filter((x) => x !== id) }));
  await batch.commit();
};

export const saveSection = (id, data) => setDoc(doc(db, COL.sections, id), data, { merge: true });

export const saveMenu = (id, data) => setDoc(doc(db, COL.menus, id), data, { merge: true });

export const createMenu = async (name, dishIds = []) => {
  const id = `${slug(name) || 'menu'}-${Date.now().toString(36)}`;
  await setDoc(doc(db, COL.menus, id), { name, dishIds, createdAt: serverTimestamp() });
  return id;
};

export const removeMenu = (id) => deleteDoc(doc(db, COL.menus, id));

export const saveConfig = (data) => setDoc(doc(db, COL.config, CONFIG_ID), data, { merge: true });

// Adds several dishes at once and appends them to the live menu.
export const bulkAddDishes = async (items, existingDishes, liveMenu) => {
  const batch = writeBatch(db);
  const nextOrder = {};
  const stamp = Date.now().toString(36);
  const ids = items.map((it, i) => {
    if (nextOrder[it.section] === undefined) {
      nextOrder[it.section] = Math.max(-1, ...existingDishes.filter((d) => d.section === it.section).map((d) => d.order ?? 0)) + 1;
    }
    const id = `${it.section}--${stamp}${i}`;
    batch.set(doc(db, COL.dishes, id), {
      section: it.section, order: nextOrder[it.section]++, soldOut: false, i18n: it.i18n, updatedAt: serverTimestamp(),
    });
    return id;
  });
  if (liveMenu) batch.update(doc(db, COL.menus, liveMenu.id), { dishIds: [...(liveMenu.dishIds || []), ...ids] });
  await batch.commit();
  return ids.length;
};

// ---------- reservations ----------

// A guest's request; the kitchen confirms by phone. Fields mirror firestore.rules.
// Returns the new reservation's id (used as the ticket code).
export const createReservation = async ({ type, name, phone, email, date, time, guests, note, lang }) => {
  const ref = doc(collection(db, COL.reservations));
  await setDoc(ref, {
    type, name: name.trim(), phone: phone.trim(), email: email.trim().toLowerCase(), date, time, guests: Number(guests),
    note: (note || '').trim(), lang, status: 'new', createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const subscribeReservations = (cb, onError) =>
  onSnapshot(collection(db, COL.reservations), (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, onError);

export const setReservationStatus = (id, status) => updateDoc(doc(db, COL.reservations, id), { status });

export const updateReservation = (id, data) => updateDoc(doc(db, COL.reservations, id), data);
